// The RingCentral phone (decision D4): the Web Phone SDK, ringcentral-web-phone 2.6.0, run in the SharedWorker with
// its WebRTC delegated to a console tab ("SharedWorker WebPhone with delegated WebRTC" in the SDK's README). The SDK
// owns SIP signalling, the call sessions, re-INVITEs and hold; media-host.ts in a tab owns the RTCPeerConnection, the
// microphone and the speaker.
//
//  - The SIP registration comes from core (POST /api/softphone/sip): the browser never has a RingCentral API token.
//    It is kept in this worker's memory only.
//  - autoAnswer is off: a call is answered only when the operator answers it.
//  - One instance per browser (the SDK's default instanceId, shared): the newest registration of an extension gets its
//    inbound calls, which keeps an operator within RingCentral's 5 registrations per extension.
//  - A lost WebSocket is restarted with backoff (2 s doubling to 60 s), then answered calls are re-INVITEd (the SDK's
//    recovery guidance). A call whose tab went away is re-INVITEd onto the tab in use.
//
// Nothing here has been run against RingCentral yet: docs/ringcentral/SOFTPHONE.md, "Verification".

import WebPhone from 'ringcentral-web-phone';
import type CallSession from 'ringcentral-web-phone/call-session/index';
import type InboundCallSession from 'ringcentral-web-phone/call-session/inbound';
import type OutboundCallSession from 'ringcentral-web-phone/call-session/outbound';
import type { SipInfo, WebPhoneOptions, WebRtcSession, WebRtcSessionFactory } from 'ringcentral-web-phone/types';
import type { ControlAction, ControlArgs, EngineHost, MediaArgs, MediaOp, PhoneCall, PhoneEngine, Registration } from './engine.ts';

/** What the engine uses of the SDK (tests give it a stand-in). */
export interface WebPhoneLike {
  callSessions: CallSession[];
  disposed: boolean;
  sipClient: unknown;
  start(): Promise<void>;
  dispose(): Promise<void>;
  call(callee: string, callerId?: string): Promise<OutboundCallSession>;
  on(event: 'inboundCall' | 'outboundCall', fn: (s: CallSession) => void): void;
}
export type WebPhoneCtor = new (o: WebPhoneOptions) => WebPhoneLike;

/** SIP numbers come as 14805550134, +14805550134, or an extension. */
export function e164ish(n: string | null | undefined): string | null {
  const s = (n ?? '').trim();
  if (!s) return null;
  if (s.startsWith('+')) return s;
  const d = s.replace(/\D/g, '');
  if (/^1\d{10}$/.test(d)) return `+${d}`;
  if (/^\d{10}$/.test(d)) return `+1${d}`;
  return d || s;
}

/** The SOC line a call is on, as a phone number; a SIP user or device id (what WebRTC often reports) is no use to an agent. */
export function lineNumber(n: string | null | undefined): string | null {
  const e = e164ish(n);
  return e && /^\+[1-9]\d{6,14}$/.test(e) ? e : null;
}

/** The SDK wants digits (16506668888) or an extension. */
const dialable = (n: string) => n.replace(/[^\d*#]/g, '');

interface Tracked {
  s: CallSession;
  startedAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  endReason: string | null;
  held: boolean;
  muted: boolean;
  consultFor: string | null;
  /** The tab running this call's WebRTC. */
  tab: string | null;
}

const ENDED_KEEP_MS = 8000;

export class RingCentralEngine implements PhoneEngine {
  readonly name = 'ringcentral' as const;
  readonly simulated = false;
  readonly recordsOwnControls = false;
  private wp: WebPhoneLike | null = null;
  private tracked = new Map<string, Tracked>();
  private warm = new Map<string, { complete: () => Promise<void>; cancel: () => Promise<void>; original: string }>();
  private subs = new Set<() => void>();
  private status: ((r: Registration, e: string | null) => void) | null = null;
  private reconnecting = false;
  private stopped = false;

  constructor(
    private readonly host: EngineHost,
    private readonly Ctor: WebPhoneCtor = WebPhone as unknown as WebPhoneCtor,
    private readonly sleep: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
  ) {}

  onChange(cb: () => void): void {
    this.subs.add(cb);
  }

  onStatus(cb: (r: Registration, e: string | null) => void): void {
    this.status = cb;
  }

  private changed() {
    for (const cb of this.subs) cb();
  }

  calls(): PhoneCall[] {
    return [...this.tracked.values()].map((t) => this.view(t));
  }

  private view(t: Tracked): PhoneCall {
    const s = t.s;
    const ended = s.state === 'disposed' || s.state === 'failed' || !!t.endedAt;
    return {
      id: s.callId,
      telephonySessionId: s.sessionId ?? null,
      direction: s.direction,
      state: ended ? 'ended' : s.state === 'answered' ? (t.held ? 'held' : 'connected') : 'ringing',
      remote: e164ish(s.remoteNumber),
      line: lineNumber(s.localNumber),
      muted: t.muted,
      startedAt: t.startedAt,
      answeredAt: t.answeredAt,
      endedAt: t.endedAt,
      endReason: t.endReason,
      consultFor: t.consultFor,
    };
  }

  /** Delegated WebRTC: each operation runs in a tab; the first runs wherever the operator is. */
  private readonly factory: WebRtcSessionFactory = (ctx) => {
    const base: MediaArgs = { callId: ctx.callId, direction: ctx.direction, stunServers: ctx.stunServers };
    const run = async (op: MediaOp, extra: Partial<MediaArgs> = {}) => {
      const t = this.tracked.get(ctx.callId);
      const r = await this.host.media(op, { ...base, ...extra }, t?.tab ?? null);
      if (t) t.tab = r.tab;
      return r.data;
    };
    const session: WebRtcSession = {
      createOffer: async (o) => String(await run('createOffer', { iceRestart: o?.iceRestart })),
      createAnswer: async (offer) => String(await run('createAnswer', { sdp: offer })),
      applyAnswer: async (answer) => void (await run('applyAnswer', { sdp: answer })),
      changeInputDevice: async (deviceId) => void (await run('changeInputDevice', { deviceId })),
      changeOutputDevice: async (deviceId) => void (await run('changeOutputDevice', { deviceId })),
      setMuted: (muted) => void run('setMuted', { muted }).catch(() => undefined),
      sendDtmf: (tones, duration, interToneGap) => void run('sendDtmf', { tones, duration, interToneGap }).catch(() => undefined),
      dispose: () => void run('dispose').catch(() => undefined),
    };
    return session;
  };

  private track(s: CallSession, consultFor: string | null = null): Tracked {
    const known = this.tracked.get(s.callId);
    if (known && known.s === s) return known;
    const t: Tracked = { s, startedAt: new Date().toISOString(), answeredAt: null, endedAt: null, endReason: null, held: false, muted: false, consultFor, tab: null };
    this.tracked.set(s.callId, t);
    s.on('answered', () => {
      t.answeredAt ??= new Date().toISOString();
      this.changed();
    });
    const end = (reason: string | null) => {
      if (t.endedAt) return;
      t.endedAt = new Date().toISOString();
      t.endReason = reason;
      this.changed();
      setTimeout(() => {
        if (this.tracked.get(s.callId) === t) this.tracked.delete(s.callId);
        this.changed();
      }, ENDED_KEEP_MS);
    };
    // An incoming call that stops ringing here without being answered here: answered on another device (the desk phone)
    // or the caller gave up. The server's records say which.
    s.on('disposed', () => end(t.endReason ?? (s.direction === 'inbound' && !t.answeredAt ? 'StoppedRinging' : null)));
    s.on('failed', () => end('Failed'));
    this.changed();
    return t;
  }

  async start(): Promise<void> {
    this.stopped = false;
    const r = await this.host.http<{ sipInfo: SipInfo }>('POST', '/api/softphone/sip');
    const wp = new this.Ctor({ sipInfo: r.sipInfo, autoAnswer: false, webRtcSessionFactory: this.factory });
    wp.on('inboundCall', (s) => void this.track(s));
    wp.on('outboundCall', (s) => void this.track(s));
    await wp.start();
    this.wp = wp;
    this.watchSocket();
  }

  async stop(): Promise<void> {
    this.stopped = true;
    const wp = this.wp;
    this.wp = null;
    this.tracked.clear();
    this.changed();
    await wp?.dispose().catch(() => undefined);
  }

  /** The SDK doesn't detect a broken connection; its socket closing does. */
  private watchSocket() {
    const wsc = (this.wp?.sipClient as { wsc?: { addEventListener(t: 'close', fn: () => void): void } } | undefined)?.wsc;
    wsc?.addEventListener('close', () => {
      if (!this.stopped && !this.wp?.disposed) void this.reconnect();
    });
  }

  private async reconnect() {
    if (this.reconnecting) return;
    this.reconnecting = true;
    this.status?.('reconnecting', null);
    let delay = 2000;
    try {
      for (;;) {
        await this.sleep(delay);
        if (this.stopped) return;
        try {
          await this.recover();
          this.status?.('registered', null);
          return;
        } catch (err) {
          this.status?.('reconnecting', (err as Error).message);
          delay = Math.min(delay * 2, 60_000);
        }
      }
    } finally {
      this.reconnecting = false;
    }
  }

  /** A new SIP connection and registration; answered calls are re-INVITEd (held ones stay held). */
  async recover(): Promise<void> {
    const wp = this.wp;
    if (!wp) throw new Error('the phone is not started');
    await wp.start();
    this.watchSocket();
    for (const t of this.tracked.values()) if (t.s.state === 'answered' && !t.endedAt) await t.s.reInvite(!t.held).catch(() => undefined);
  }

  /** A call's tab went away: its media moves to the tab in use. */
  tabsChanged(tabIds: string[]): void {
    const live = new Set(tabIds);
    for (const t of this.tracked.values()) {
      if (!t.tab || live.has(t.tab) || t.endedAt || t.s.state !== 'answered') continue;
      t.tab = null;
      void t.s.reInvite(!t.held).catch(() => undefined);
    }
  }

  async dial(to: string, callerId: string | null): Promise<PhoneCall | null> {
    const wp = this.wp;
    if (!wp) throw new Error('the phone is not started');
    const s = await wp.call(dialable(to), callerId ? dialable(callerId) : undefined);
    if (s.state === 'failed' || s.state === 'disposed') throw new Error('RingCentral could not place the call (check the number and the extension’s emergency address)');
    return this.view(this.track(s));
  }

  private session(callId: string): Tracked {
    const t = this.tracked.get(callId);
    if (!t || t.endedAt || t.s.state === 'disposed') throw new Error('that call is not on this phone');
    return t;
  }

  async control(callId: string, action: ControlAction, args: ControlArgs): Promise<{ consultCallId?: string }> {
    const t = this.session(callId);
    const s = t.s;
    const inbound = s as InboundCallSession;
    const answered = s.state === 'answered';
    const need = (ok: boolean, why: string) => {
      if (!ok) throw new Error(why);
    };
    switch (action) {
      case 'answer':
        need(s.direction === 'inbound' && !answered, 'only a ringing incoming call can be answered');
        await inbound.answer();
        t.answeredAt ??= new Date().toISOString();
        break;
      case 'decline':
        need(s.direction === 'inbound' && !answered, 'only a ringing incoming call can be declined');
        t.endReason = 'Rejected';
        await inbound.decline();
        break;
      case 'to_voicemail':
        need(s.direction === 'inbound' && !answered, 'only a ringing incoming call can go to voicemail');
        t.endReason = 'Voicemail';
        await inbound.toVoicemail();
        break;
      case 'hangup':
        if (!answered && s.direction === 'outbound') await (s as OutboundCallSession).cancel();
        else await s.hangup();
        break;
      case 'mute':
      case 'unmute':
        need(answered, 'mute needs an answered call');
        if (action === 'mute') s.mute();
        else s.unmute();
        t.muted = action === 'mute';
        break;
      case 'hold':
      case 'unhold':
        need(answered, 'hold needs an answered call');
        if (action === 'hold') await s.hold();
        else await s.unhold();
        t.held = action === 'hold';
        break;
      case 'dtmf':
        need(answered, 'keys need an answered call');
        s.sendDtmf(args.digits ?? '');
        break;
      case 'transfer':
        need(answered && !!args.to, 'transfer needs an answered call and a target');
        t.endReason = 'BlindTransfer';
        await s.transfer(dialable(args.to!));
        break;
      case 'warm_transfer_start': {
        need(answered && !!args.to, 'transfer needs an answered call and a target');
        const w = await s.warmTransfer(dialable(args.to!));
        t.held = true;
        const c = this.track(w.newSession, s.callId);
        this.warm.set(w.newSession.callId, { complete: w.complete, cancel: w.cancel, original: s.callId });
        this.changed();
        return { consultCallId: c.s.callId };
      }
      case 'warm_transfer_complete': {
        const w = this.warm.get(callId);
        need(!!w, 'this is not a transfer consultation');
        await w!.complete();
        this.warm.delete(callId);
        const o = this.tracked.get(w!.original);
        if (o) o.endReason = 'AttendedTransfer';
        t.endReason = 'AttendedTransfer';
        break;
      }
      case 'warm_transfer_cancel': {
        const w = this.warm.get(callId);
        need(!!w, 'this is not a transfer consultation');
        await w!.cancel();
        this.warm.delete(callId);
        // Back to the caller, as on the simulator.
        const o = this.tracked.get(w!.original);
        if (o && o.held && o.s.state === 'answered') {
          await o.s.unhold();
          o.held = false;
        }
        break;
      }
    }
    this.changed();
    return {};
  }

  /** RingCentral calls are not pushed over the stream: the phone hears them over SIP. */
  serverMessage(): void {}
}
